/**
 * React Navigation linking configuration.
 *
 * Drives URL-based routing on web (so every screen has a real address,
 * back/forward + refresh + sharable links work).
 *
 * URL surface mirrors the legacy web app's routes (useRoutes)
 * so existing share links, push-notification URLs, and bookmarks keep working.
 *
 * Design notes
 * ─────────────
 * - **Web-only.** `linkingOptions` resolves to `undefined` on native so this
 *   config never fires there. Native deep linking continues to be handled by
 *   `RootNavigator`'s existing `Linking.getInitialURL()` + `addEventListener`
 *   useEffects and by direct `navigationRef.navigate()` calls from the push
 *   notification response handlers (`handleNotificationResponse` etc.).
 *   Those need to run side effects (signOut + dispatch + setEmergencyCardDeepLink
 *   + setPendingRegisterPrefill etc.) that the declarative linking config
 *   can't express; keeping native off this codepath avoids a double-process.
 * - **Modals are not URL-routable.** Screens registered with `presentation: 'modal'`
 *   in `RootNavigator` (Ingestion, InteractiveSurvey, OnboardingReplay) are
 *   intentionally omitted from the screens map — they're internal navigation
 *   targets opened by buttons / push handlers, not bookmarkable destinations.
 *
 * Adding a new screen?
 * ─────────────────────
 * 1. Add the screen to the relevant Stack.Screen / Tab.Screen.
 * 2. Add its `name` → `'path/with/:params'` entry in the `screens` map below.
 * 3. (optional) Add the path to the legacy useRoutes table if you also want it
 *    in the desktop sidebar / route guards.
 */

import { getPathFromState, getStateFromPath, type LinkingOptions } from '@react-navigation/native';
import { isWeb, isElectron } from '../utilities/platform';
import { rewriteLegacyPartnerPath } from './partnerWorkspace';
import { ALL_TOP_LEVEL_TAB_META } from '../screens/admin/topLevel/topLevelTabMetadata';
import { ALL_TAB_META as ADMIN_SETTINGS_TAB_META } from '../screens/admin/settings/tabMetadata';

// ── Project-scoped screens ───────────────────────────────────────────────────
//
// These all match `project/:projectId/...` in the path config below. We normalize
// nav params for any of these before letting react-navigation build a URL — see
// `normalizeProjectParams` for why.
//
// Keep in sync with the entries in `Plans.screens` that begin with `project/:projectId`.
const PROJECT_SCOPED_SCREENS = new Set<string>([
  'ProjectChat',
  'ProjectHistory',
  'ImportedConversation',
  'Conversation',
  'EllieVoice',
  'ProjectCalendar',
  'ProjectCalendarSettings',
  'ProjectAppleCalendarSettings',
  'ProjectMedications',
  'ProjectContacts',
  'ProjectMembers',
  'ProjectTeam',
  'ProjectMemberChat',
  'ProjectCommunicate',
  'ProjectFiles',
  'ProjectNotes',
  'ProjectInbox',
  'ProjectFasten',
  'ProjectSettings',
  'ProjectHealthIntegration',
  'ProjectSurveys',
  'ProjectBenefits',
  'SiriShortcuts',
  'ProjectVault',
  'ProjectFeatures',
  'EmergencySOSSetup',
  'ProjectReminders',
  'ProjectTimeline',
  'ProjectSmartReminders',
  'ProjectHealthAlerts',
  'Wellness',
  'ProjectEmailSettings',
  'ChatImport',
  'ProjectSnapshots',
  'ProjectDiagnoses',
  'ProjectWeeklyBriefing',
  'ProjectCadenceSession',
  // Newly mapped. Without membership here their `{ project: <obj> }` params
  // would serialize to `project/undefined/...?project=null`, which is the bug
  // normalizeProjectParams exists to prevent.
  'PlanTasks',
  'AgentSuggestions',
  'DeletePlan',
  'ProjectInboxSetup',
  'ProjectHealthProfile',
  'ProjectKnowledgeCoverage',
]);

// Param keys we never want appearing as query strings on a built URL — they
// either serialize to garbage (full project objects → `[object Object]`) or
// are duplicates of values already in the path (`projectName`).
const STRIPPED_PARAM_KEYS = new Set<string>(['project', 'projectName']);

/**
 * Derive a usable `projectId` from whatever params the caller passed.
 *
 * Many existing call sites pass `{ project: activeProject }` (the full Redux
 * project object) instead of — or in addition to — a primitive `projectId`.
 * This worked on native because react-navigation just hands params to the
 * destination screen, but on web it broke URL generation: the linking
 * builder couldn't find `:projectId`, so it inserted the literal string
 * "undefined" into the path AND dumped `project` and `projectName` into the
 * query string as `[object Object]` / empty strings. On refresh those bad
 * URLs parsed back into bad params and every API request fired with
 * `projectId="undefined"` or `project="null"`.
 *
 * Rather than rewrite every call site, we fix it once here.
 */
function deriveProjectId(params: any): string | number | undefined {
  if (!params) return undefined;
  if (params.projectId != null && params.projectId !== '' && params.projectId !== 'undefined' && params.projectId !== 'null') {
    return params.projectId;
  }
  const proj = params.project;
  if (proj && typeof proj === 'object') {
    return proj.id ?? proj.project_id ?? undefined;
  }
  return undefined;
}

/**
 * Walks a navigation state tree and rewrites params on project-scoped routes:
 *   - sets `projectId` derived from `project.id || project.project_id`
 *   - strips the `project` object, `projectName`, and any junk-typed values
 *     (null / undefined / empty / object) so they don't appear in the query
 *     string
 *   - if no valid `projectId` can be derived, swaps the route to
 *     `ProjectChat` for URL purposes — that way the address bar shows
 *     `/projects` instead of `/project/undefined/calendar`. The actual
 *     navigation state is unchanged (this transform is URL-only); the
 *     destination screen's existing `projectId == null` guards keep API
 *     calls from firing with bad ids.
 *
 * Pure function — never mutates the input state.
 */
function normalizeProjectParams(state: any, parentRouteName?: string): any {
  if (!state || typeof state !== 'object' || !Array.isArray(state.routes)) return state;
  let mutated = false;
  const routes = state.routes.map((route: any) => {
    let next = route;
    if (PROJECT_SCOPED_SCREENS.has(route?.name) && !(route.name === 'SiriShortcuts' && parentRouteName === 'Settings')) {
      const projectId = deriveProjectId(route?.params);
      if (projectId == null) {
        // No usable project — fall back to the projects list URL.
        next = { ...route, name: 'ProjectChat', params: undefined };
        mutated = true;
      } else if (route.params) {
        const cleaned: Record<string, any> = {};
        for (const [k, v] of Object.entries(route.params)) {
          if (STRIPPED_PARAM_KEYS.has(k)) continue;
          // Drop literal junk that would either round-trip badly or serialize
          // as `[object Object]` in the query string.
          if (
            v === null ||
            v === undefined ||
            v === '' ||
            v === 'undefined' ||
            v === 'null' ||
            (typeof v === 'object' && !Array.isArray(v))
          ) continue;
          cleaned[k] = v;
        }
        cleaned.projectId = projectId;
        next = { ...route, params: cleaned };
        mutated = true;
      }
    }
    if (route?.state) {
      const childState = normalizeProjectParams(route.state, route.name);
      if (childState !== route.state) {
        next = { ...next, state: childState };
        mutated = true;
      }
    }
    return next;
  });
  return mutated ? { ...state, routes } : state;
}

/**
 * Inverse of `normalizeProjectParams` for refresh / back-button reloads.
 *
 * Two transforms, both URL-driven (only matter when react-navigation is
 * parsing `window.location` into a fresh state tree):
 *
 *   1. **Strip literal junk** — `projectId="undefined"` / `project="null"` /
 *      `projectName=""` from older bookmarks decode back into params with the
 *      string values "undefined" / "null" / "". Drop them so downstream code
 *      sees a real `undefined`.
 *   2. **Redirect dead project routes** — a project-scoped route with no
 *      derivable `projectId` after cleanup can't load anything; the screen
 *      would mount and fire API calls against `projectId === undefined`,
 *      explaining the "lots of requests failing" symptom. Swap the route to
 *      `ProjectChat` so the user lands on a usable screen instead of a
 *      broken one. Pairs with the `history.replaceState` call in
 *      `linking.getStateFromPath` below to also fix the URL bar.
 */
function sanitizeStateFromPath(state: any, parentRouteName?: string): any {
  if (!state || typeof state !== 'object' || !Array.isArray(state.routes)) return state;
  let mutated = false;
  const routes = state.routes.map((route: any) => {
    let next = route;
    // Resolve this legacy URL before the navigator mounts. A mount-time
    // replace can be superseded by restoration of the initial URL state.
    if (route?.name === 'ProjectCommunicate' && route?.params?.initialTab === 'inbox') {
      const { initialTab, ...params } = route.params;
      next = { ...route, name: 'ProjectInbox', params };
      mutated = true;
    }
    if (route?.name === 'ProjectFiles' && (route.params?.initialTab === 'notes' || route.params?.tab === 'notes'
      || route.params?.openNoteId != null || route.params?.openNewNote === true || route.params?.openNewNote === 'true'
      || route.params?.shortcutAction != null || route.params?.openTemplateId != null)) {
      const { initialTab, tab, ...params } = route.params;
      next = { ...route, name: 'ProjectNotes', params };
      mutated = true;
    }
    // Siri has both account and plan entries with the same route name.
    if (PROJECT_SCOPED_SCREENS.has(next?.name) && !(next.name === 'SiriShortcuts' && parentRouteName === 'Settings')) {
      const cleaned: Record<string, any> = { ...(next?.params ?? {}) };
      let didStrip = false;
      for (const k of Object.keys(cleaned)) {
        const v = cleaned[k];
        if (v === 'undefined' || v === 'null' || v === '') {
          delete cleaned[k];
          didStrip = true;
        }
      }
      // Cleanup may have removed the projectId entirely. The screen needs it
      // to do anything, so kick the route over to the plan chat root instead of
      // letting it mount in a broken state.
      if (cleaned.projectId == null) {
        next = { name: 'ProjectChat', params: undefined } as any;
        mutated = true;
      } else if (didStrip) {
        next = { ...next, params: cleaned };
        mutated = true;
      }
    }
    if (route.state) {
      const childState = sanitizeStateFromPath(route.state, route.name);
      if (childState !== route.state) {
        next = { ...next, state: childState };
        mutated = true;
      }
    }
    return next;
  });
  return mutated ? { ...state, routes } : state;
}

// Belt-and-suspenders for URL generation: no route should serialize an
// object-valued param into the query string — it renders as `[object Object]`.
// normalizeProjectParams covers project-scoped routes; this catches every other
// route (e.g. admin AdminEditUser, navigated with a full `user` object). URL-only:
// the in-memory navigation params are untouched, so the destination screen still
// receives the object via route.params.
function stripObjectParams(state: any): any {
  if (!state || typeof state !== 'object' || !Array.isArray(state.routes)) return state;
  let mutated = false;
  const routes = state.routes.map((route: any) => {
    let next = route;
    if (route?.params && typeof route.params === 'object') {
      let didStrip = false;
      const cleaned: Record<string, any> = {};
      for (const [k, v] of Object.entries(route.params)) {
        if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
          didStrip = true;
          continue;
        }
        cleaned[k] = v;
      }
      if (didStrip) {
        next = { ...route, params: cleaned };
        mutated = true;
      }
    }
    if (route?.state) {
      const childState = stripObjectParams(route.state);
      if (childState !== route.state) {
        next = { ...next, state: childState };
        mutated = true;
      }
    }
    return next;
  });
  return mutated ? { ...state, routes } : state;
}

// ── URL prefixes ─────────────────────────────────────────────────────────────
//
// On native, these are matched against incoming URLs (universal links + custom
// scheme). On web, they are ignored — react-navigation just looks at
// `window.location.pathname`.
const PREFIXES = [
  // Production universal links
  'https://app.eleplan.com',
  // Development universal links
  'https://development.eleplan.org',
  // Short link domains (server redirects to the destination, but on web we
  // can render the destination directly once the server resolves the slug)
  'https://eleplan.link',
  'https://development.eleplan.link',
  // Custom scheme (matches `expo.scheme` in app.json)
  'com.eleplan.app://',
];

// ── Screen → path map ────────────────────────────────────────────────────────
//
// Loose typing (`any`) on purpose — the strict `PathConfigMap` requires full
// route param typing across all navigators, which we don't track centrally yet.
// Tighten in a follow-up once route param types live in one place.

// ── Admin console paths ──────────────────────────────────────────────────────
//
// Generated from the same two metadata registries AdminStack mounts its screens
// from, so "add a tab = one registry line" stays true and a new tab cannot drift
// out of the URL map. Hand-listing 122 routes here would rot on the first PR.
//
// Every path is namespaced under `admin/` so it cannot collide with the segments
// the Main stack already claims (home, ai, projects, settings, clients, work, …).
// SupportAdminInbox/SupportAdminChat are registered in three stacks; their admin
// copies get `admin/`-prefixed patterns for that reason.

/** AdminIntentApprovals → intent-approvals; accountfeatures → accountfeatures. */
const kebab = (name: string): string => name
  .replace(/^Admin(Settings)?/, '')
  .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
  .toLowerCase()
  .replace(/^-+|-+$/g, '');

const SETTINGS_SUB_ROUTE_NAMES = [
  'AdminSettingsAppearance',
  // screens/admin/settings/tabRegistry.ts SETTINGS_SUB_ROUTES. Listed by name so
  // this module stays free of screen imports; linkingCoverage.test.ts asserts
  // the two lists agree.
  'AdminSettingsRuleEdit',
  'AdminSettingsPlanEdit',
  'AdminSettingsPlanTaskEdit',
  'AdminSettingsSsmParamEdit',
  'AdminSettingsDataApiKeyDetail',
  'AdminSettingsLocalClassifierGold',
];

function buildAdminScreens(): Record<string, string> {
  const screens: Record<string, string> = {
    AdminHome: 'admin',
    AdminSettings: 'admin/settings',
  };
  for (const tab of ALL_TOP_LEVEL_TAB_META) {
    const base = `admin/${kebab(tab.key)}`;
    screens[tab.routeName] = base;
    for (const sub of tab.subRoutes ?? []) {
      // `:id?` optional: a detail route reached in-app carries an object param
      // that never reaches the URL, so restoring lands on the list-shaped
      // screen rather than a detail with no subject.
      screens[sub.routeName] = `${base}/${kebab(sub.routeName)}/:id?`;
    }
  }
  for (const tab of ADMIN_SETTINGS_TAB_META) screens[tab.routeName] = `admin/settings/${kebab(tab.key)}`;
  for (const name of SETTINGS_SUB_ROUTE_NAMES) screens[name] = `admin/settings/${kebab(name)}/:id?`;
  return screens;
}

const ADMIN_SCREENS = buildAdminScreens();

/**
 * The Plans root is the plan chat before it knows which plan it opens: it has no
 * :projectId to put in the path, so it keeps the old plans URL.
 */
/** A root state whose focused route is a bare `Main`: no nested state and no target in its params. */
export function isShellMounting(state: any): boolean {
  const route = state?.routes?.[state?.index ?? (state?.routes?.length ?? 1) - 1];
  return route?.name === 'Main' && !route.state && !route.params?.screen && !route.params?.state;
}

/** The address-bar path, when it names a screen inside Main (not `/`, auth or Accounts). */
export function currentInAppPath(options: any, location: { pathname: string; search: string } | undefined =
  typeof window !== 'undefined' ? window.location : undefined): string | null {
  if (!location || location.pathname === '/' || !location.pathname) return null;
  const path = `${location.pathname}${location.search || ''}`;
  try {
    const parsed: any = getStateFromPath(path.replace(/^\//, ''), options);
    const root = parsed?.routes?.[parsed.routes.length - 1];
    return root?.name === 'Main' && root.state ? path : null;
  } catch {
    return null;
  }
}

function buildPath(state: any, options: any): string {
  const path = getPathFromState(stripObjectParams(normalizeProjectParams(state)), options);
  return path === '/project/undefined' ? '/projects' : path;
}

const linking: LinkingOptions<any> = {
  prefixes: PREFIXES,
  config: {
    initialRouteName: 'Main' as any,
    screens: {
      // ── Public (no auth required) ──────────────────────────────────
      // EmergencyCard is registered in BOTH the auth and unauth stacks so a
      // tapped Health Passport link opens the card regardless of whether
      // the viewer is signed in. The screen itself fetches by token only.
      EmergencyCard: 'emergency/:token',

      // The page an AI assistant (ChatGPT, Claude, Perplexity) sends a caregiver
      // to in order to approve a connection: /connect/authorize?request=<id>.
      // Registered in BOTH stacks for the same reason as EmergencyCard: signed
      // out it is the sign-in, and the same route (with `request` still on it)
      // becomes the consent page once they are signed in. It has to stay in
      // the browser the assistant opened (the answer ends at a URL that checks
      // that browser's cookie), so /connect/* must NOT be a universal link:
      // the app.eleplan.com apple-app-site-association (an S3 object, not in
      // this repo) needs `NOT /connect` and `NOT /connect/*` ahead of its `*`.
      // Android cannot exclude a path from its App Link filter, so the app
      // hands such a link on to the system browser instead: handOffConnectLink.
      ConnectAuthorize: 'connect/authorize',

      // ── Unauthenticated stack ──────────────────────────────────────
      Login: 'login',
      Register: 'register',
      ClaimInvite: 'claim',
      PartnerRegister: 'partners/register',
      // Co-branded partner intermediary page. Static `partners/register` above
      // is matched first, so this only catches `partners/<slug>` (e.g.
      // /partners/lovealways) and resolves the slug to the partner's branding.
      PartnerLanding: 'partners/:slug',
      MFA: 'mfa',
      // Legacy aliased `/force` → `/change-password` (legacy web supports both).
      // react-navigation only accepts a single primary path per screen here;
      // /force is handled via a redirect in the auth screen itself if needed.
      ForcePassword: 'change-password',

      // ── Authenticated root ─────────────────────────────────────────
      Accounts: 'accounts',
      // OnboardingReplay, Ingestion, InteractiveSurvey are presentation: 'modal'
      // at the root stack — see "Modals are not URL-routable" note above.

      // ── Main tab navigator ─────────────────────────────────────────
      Main: {
        screens: {
          // Client Home landing tab. First tab in TAB_ROUTES, so it's also the
          // default route when Main mounts without a deeper URL.
          Home: 'home',

          // Account-level chat tab (legacy `/` for users with multiple plans).
          AI: 'ai',

          // ── Plans stack ────────────────────────────────────────────
          Plans: {
            initialRouteName: 'ProjectChat' as any,
            screens: {
              CreatePlan: 'projects/create/chat',

              // Project routes — :projectId is the numeric id, route params
              // (openPageName, openSurveyName, openQuestionName, briefingId,
              // initialTab, openUpload, prefillDocumentType, etc.) ride along
              // as query strings and react-navigation injects them into
              // `route.params` automatically.
              // Plan home went (2026-10-06): its old links open Ellie's start screen.
              // `projects` (the old plans list URL) opens the chat root, which
              // opens the landing plan and puts its id in the URL.
              ProjectChat: {
                path: 'project/:projectId',
                alias: ['project/:projectId/chat', 'project/:projectId/home', 'projects'],
              },
              ProjectHistory:              'project/:projectId/history',
              ImportedConversation:        'project/:projectId/imported-chat/:conversationId',
              Conversation:                'project/:projectId/conversation/:conversationId?',
              // Realtime voice — scannable from the desktop QR ("take the call
              // on the go"). conversationId is optional so a bare project voice
              // link still works; when present the session continues that thread.
              EllieVoice:                  'project/:projectId/voice/:conversationId?',
              ProjectCalendar:             'project/:projectId/calendar',
              ProjectCalendarSettings:     'project/:projectId/calendar/settings',
              ProjectAppleCalendarSettings:'project/:projectId/calendar/apple',
              ProjectMedications:          'project/:projectId/medications',
              // Legacy `/people` mapped to the merged contacts+members screen.
              ProjectContacts:             'project/:projectId/people',
              // Legacy `/members` (project-scoped) — same screen, different path.
              ProjectMembers:              'project/:projectId/members',
              // Canonical Team entry (Members + Contacts + Chat tabs).
              ProjectTeam:                 'project/:projectId/team',
              // Member-to-member chat (NOT the AI chat). ProjectMemberChat is
              // the legacy direct entry; ProjectCommunicate is the canonical
              // container with Chat | Inbox sub-tabs.
              ProjectMemberChat:           'project/:projectId/member-chat',
              ProjectCommunicate:          'project/:projectId/communicate',
              ProjectFiles:                'project/:projectId/files',
              ProjectNotes:                'project/:projectId/notes',
              ProjectInbox:                'project/:projectId/inbox',
              ProjectFasten:               'project/:projectId/fasten',
              ProjectSettings:             'project/:projectId/settings',
              ProjectHealthIntegration:    'project/:projectId/health',
              ProjectSurveys:              'project/:projectId/surveys',
              ProjectBenefits:             'project/:projectId/benefits',
              SiriShortcuts:               'project/:projectId/siri-shortcuts',
              ProjectVault:                'project/:projectId/vault/:credentialId?',
              ProjectFeatures:             'project/:projectId/features',
              EmergencySOSSetup:           'project/:projectId/sos',
              ProjectReminders:            'project/:projectId/reminders',
              ProjectTimeline:             'project/:projectId/timeline',
              ProjectSmartReminders:       'project/:projectId/smart-reminders',
              ProjectHealthAlerts:         'project/:projectId/health-alerts',
              Wellness:                    'project/:projectId/wellness',
              ProjectEmailSettings:        'project/:projectId/email-settings',
              ChatImport:                  'project/:projectId/import-chats',
              ProjectSnapshots:            'project/:projectId/snapshots',
              ProjectDiagnoses:            'project/:projectId/diagnoses',
              ProjectWeeklyBriefing:       'project/:projectId/weekly-briefing',
              ProjectCadenceSession:       'project/:projectId/cadence/:sessionId',
              // Registered in the Plans stack but previously unmapped, so
              // getPathFromState fell back to the raw route name and the URL
              // could not be parsed back on refresh — see PROJECT_SCOPED_SCREENS.
              PlanTasks:                   'project/:projectId/tasks',
              AgentSuggestions:            'project/:projectId/suggestions',
              DeletePlan:                  'project/:projectId/delete',
              ProjectInboxSetup:           'project/:projectId/inbox/setup',
              ProjectHealthProfile:        'project/:projectId/health-profile',
              ProjectKnowledgeCoverage:    'project/:projectId/knowledge',
            },
          },

          // ── Settings stack ─────────────────────────────────────────
          Settings: {
            initialRouteName: 'SettingsHome' as any,
            screens: {
              SettingsHome:        'settings',
              AppScreen:           'screens/:slug',
              Profile:             'settings/profile',
              Billing:             'settings/billing',
              HealthIntegration:   'settings/health',
              SiriShortcuts:       'settings/siri-shortcuts',
              WatchSettings:       'settings/watch',
              DriveSettings:       'settings/drive',
              // Admin-only — account-wide admin chat. Gated client-side on
              // the SettingsScreen entry row (isAdmin || isEleplanAdmin) and
              // server-side on the chat endpoints; a direct URL hit is safe
              // because the screen still calls auth-gated APIs.
              AdminAccountChat:    'settings/admin-chat',
              // User-facing support entry — lazy-creates the account's
              // is_support room on first open.
              SupportChat:         'settings/support',
              // Eleplan-admin support triage. URL parses but endpoints
              // re-gate so a non-admin landing here sees an empty inbox.
              SupportAdminInbox:   'settings/support-admin',
              // chatRoomId optional — the screen renders a "Missing room
              // context" empty state when absent, matching its in-app guard.
              SupportAdminChat:    'settings/support-admin/:chatRoomId?',
            },
          },

          // ══ Partner workspace ═══════════════════════════════════════
          // AppShell mounts EITHER MainTabNavigator (customer) or
          // PartnerTabNavigator, never both, but they share this one `Main`
          // config — so a partner path must not collide with a customer one or
          // getStateFromPath resolves it to a tab the partner shell does not
          // have. `settings` and `settings/*` are taken above, which is why
          // partner account config lives under `workspace`.
          //
          // None of these existed before. React Navigation still put something
          // in the address bar — it derives a segment from the raw route name
          // for anything unconfigured — so partners saw plausible URLs like
          // `/Clients/PartnerCustomers?workspaceRoot=true` and
          // `/home/PartnerHome` (that one borrowing `home` from the CUSTOMER
          // entry above, since both shells name a tab `Home`). Those addresses
          // were write-only: getPathFromState produced them, getStateFromPath
          // returned undefined for every one, so a bookmark, a refresh, a
          // shared link or browser Back dropped the partner back at the
          // default route. On the web client that is most of what "hard to
          // navigate" meant.
          //
          // Families replaced Clients + Work on 2026-10-07. The old addresses
          // (/clients, /work, /funding, /funding/planner/:id,
          // /work/intakes/:id/survey …) are rewritten by
          // rewriteLegacyPartnerPath in getStateFromPath below, so emailed
          // and bookmarked links land on the same family instead of the
          // default route.
          Families: {
            initialRouteName: 'PartnerFamilies' as any,
            screens: {
              PartnerFamilies:        'families',
              // Static `families/*` segments are distinct from the workspace,
              // which is singular `family/:familyId` so no family id can
              // collide with them. familyId is `i-<invite>`, `c-<account>` or
              // `p-<plan>` (see findPartnerFamily); `?tab=` picks the tab.
              PartnerInvites:         'families/invitations',
              PartnerProjectCreation: 'families/add-client',
              PartnerFamily:          'family/:familyId',
              PartnerIntakeSurvey:    'family-intake/:intakeId/fact-finder',
              PartnerIntakeForm:      'family-intake/:intakeId/form',
              PartnerCustomerDetail:  'clients/:customerCognitoId',
              PartnerCadencePair:     'clients/:customerCognitoId/cadence',
              PartnerProjects:        'plans',
              PartnerPlanDetail:      'plans/:projectId',
              // The standalone planner (from a plan or a client record). Its
              // subject is an intake OR a plan, so plan mode rides as
              // `?projectId=`.
              ANPPlanner:             'planner/:inviteId?',
            },
          },
          Funding: {
            initialRouteName: 'ANPSimulations' as any,
            screens: {
              ANPSimulations:         'funding-plans',
            },
          },
          Inbox: {
            initialRouteName: 'PartnerMessages' as any,
            screens: {
              PartnerMessages:    'inbox',
              PartnerMemberChat:  'inbox/member',
              PartnerAccountChat: 'inbox/team',
              // Support is a conversation, so it lives with the conversations.
              // It used to be registered in two stacks and opened as two
              // independent instances depending on the route in.
              PartnerSupportChat: 'inbox/support',
            },
          },
          More: {
            initialRouteName: 'PartnerMore' as any,
            screens: {
              // The phone overflow menu. On desktop the sidebar lists these
              // inline and this screen is never rendered.
              PartnerMore:       'more',
              PartnerDocsRoot:   'documents',
              // Static segment before the param so `documents/files` is not
              // swallowed by `documents/:customerAccountId`.
              PartnerDocsFiles:  'documents/files',
              PartnerDocsClient: 'documents/:customerAccountId',
              PartnerMembers:    'team',
              PartnerInsights:   'insights',
              PartnerEllie:      'assistant',
              // `settings` belongs to the customer shell above.
              PartnerSettings:   'workspace',
              Profile:           'workspace/profile',
              SupportAdminInbox: 'workspace/support-admin',
              // chatRoomId REQUIRED, unlike the customer entry above. With it
              // optional, `:chatRoomId?` matches the empty segment and swallows
              // the inbox path — getStateFromPath('/settings/support-admin')
              // returns SupportAdminChat, not SupportAdminInbox, which is why
              // the customer support inbox has never been reachable by URL.
              // The inbox is the listed destination here and the chat is opened
              // from it, so the room id is part of the address.
              SupportAdminChat:  'workspace/support-admin/:chatRoomId',
            },
          },
        },
      },

      // ── Admin console ──────────────────────────────────────────────
      // A root-stack sibling of Main (RootNavigator.tsx). Paths are generated
      // from the admin registries — see buildAdminScreens above. Entering here
      // from a restored URL re-runs the passkey assertion (AdminStack), because
      // useAdminConsoleGate's per-entry prompt is deliberate.
      Admin: {
        initialRouteName: 'AdminHome' as any,
        screens: ADMIN_SCREENS,
      },
    },
  },
  /**
   * Build the URL for a given navigation state. We pre-process the state to
   * normalize project-scoped params (see `normalizeProjectParams`) before
   * delegating to react-navigation's default builder. Without this, callers
   * passing `{ project: <obj> }` produced URLs like
   * `project/undefined/calendar?project=null&projectName=` which then broke
   * every API request on refresh.
   */
  getPathFromState(state, options) {
    // The signed-in shell (Main) is lazy. While it mounts, the root state is a
    // bare `Main` with nothing nested, whose path is `/` — so a link or refresh
    // at /screens/x or /settings/y briefly rewrote the bar to `/`. A fast boot
    // writes the real path back when the nested screens report in; a slow one
    // sometimes never does, the bar stays on `/`, and a refresh lands on Home.
    // While the shell has not reported a screen yet, keep a bar that already
    // names an in-app screen.
    if (isShellMounting(state)) {
      const current = currentInAppPath(options);
      if (current) return current;
    }
    return buildPath(state, options);
  },
  /**
   * Parse an incoming URL into navigation state. Calls the default parser,
   * sanitizes (strip junk literals, redirect dead project routes), then —
   * if the sanitized state no longer matches the URL it came from — quietly
   * `history.replaceState` to the canonical URL.
   *
   * Without the replaceState the browser bar stays stuck on the broken URL
   * forever (refresh = same broken URL = same broken state); with it, the
   * page self-heals on load: a stale `/project/undefined/calendar?project=null`
   * link rewrites to `/projects` immediately and the user sees the projects
   * list instead of a broken calendar firing failed API calls.
   */
  getStateFromPath(incomingPath, options) {
    // Families replaced the partner Clients/Work shell; old partner links are
    // rewritten to their new address before parsing, and the bar follows.
    const legacy = rewriteLegacyPartnerPath(incomingPath);
    const path = legacy ?? incomingPath;
    if (legacy && typeof window !== 'undefined') {
      try { window.history.replaceState({}, '', legacy); } catch { /* no History API */ }
    }
    const raw = getStateFromPath(path, options);
    const sanitized = sanitizeStateFromPath(raw);
    if (typeof window !== 'undefined' && sanitized && sanitized !== raw) {
      try {
        const canonical = buildPath(sanitized, options);
        if (canonical) {
          const target = canonical.startsWith('/') ? canonical : '/' + canonical;
          const current = window.location.pathname + window.location.search + window.location.hash;
          if (target !== current) {
            window.history.replaceState({}, '', target);
          }
        }
      } catch {
        // History API not available / canonicalization failed — leave URL as-is.
      }
    }
    return sanitized;
  },
};

/**
 * `linking` prop for `<NavigationContainer>`.
 *
 * Enabled on browser web only. Disabled on:
 *   • Native iOS/Android — uses imperative `Linking` listeners in RootNavigator.
 *   • Electron — there is no URL bar to sync to, AND enabling it actively breaks
 *     the app: navigating to `Login` etc. calls `history.pushState('/login')`,
 *     which under the `file://` scheme rewrites the document's effective base
 *     URL. Webpack's relative `publicPath: './'` then resolves lazy-chunk
 *     requests against `file:///login` instead of the dist folder, so every
 *     `React.lazy()`-wrapped screen 404s with:
 *        "Loading chunk NNNN failed. (error: file:///NNNN.bundle.<hash>.js)"
 *     Skipping `linking` keeps the document URL pinned to the original
 *     `index.html`, which is what webpack's chunk loader needs to compute the
 *     right path. Deep links from custom protocol (`eleplan://`) still work via
 *     the `deep-link` IPC handler in main.ts → `Linking.addEventListener` in
 *     RootNavigator.
 */
export const linkingOptions: LinkingOptions<any> | undefined =
  isWeb && !isElectron ? linking : undefined;

/**
 * `documentTitle` prop for `<NavigationContainer>` — updates the browser tab
 * title on web. Pulls `options.title` if a screen sets one, else prettifies
 * the route name (e.g. `ProjectChat` → `Project Chat`). No-op on native.
 */
export const documentTitle = {
  enabled: isWeb,
  formatter: (options: any, route: { name?: string } | undefined): string => {
    const title = options?.title || prettifyRouteName(route?.name);
    return title ? `Eleplan — ${title}` : 'Eleplan';
  },
};

function prettifyRouteName(name: string | undefined): string {
  if (!name) return '';
  return name.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
}
